import React, { useEffect, useMemo, useState } from "react";

import { format } from "date-fns";
import isContainer from "is-container";
import { isBoolean, isNumber } from "lodash";
import { MemberStaffWithoutTimezoneId } from "Models/members/member.staff";
import { useTranslation } from "react-i18next";
import { FiUpload } from "react-icons/fi";
import { GrDocumentText } from "react-icons/gr";
import * as XLSX from "xlsx";

import { CSVValueProps } from "../../../../../../context/onBoardingContext";
import { useAppState } from "../../../../../../context/StateContext";
import useActionOnBoarding from "../../../../../../hooks/useActionOnBoarding";
import useOnBoardingContext from "../../../../../../hooks/useOnBoardingContext";
import { AccessSettings } from "../../../../../../models/access.settings";
import { Fleet } from "../../../../../../models/fleet";
import { FleetType } from "../../../../../../models/fleet.type";
import { Instruction } from "../../../../../../models/instruction";
import {
  ILocationSymbol,
  IExceptionSymbol,
  IDescriptiveSymbol,
} from "../../../../../../models/inventory";
import { Item } from "../../../../../../models/item";
import { ItemTypes } from "../../../../../../models/item.types";
import { JobMode } from "../../../../../../models/job.mode";
import { Room } from "../../../../../../models/room";
import { Service } from "../../../../../../models/service";
import { ServiceBooking as Material } from "../../../../../../models/service.booking";
import { Shipment } from "../../../../../../models/shipments";
import { Staff } from "../../../../../../models/staff";
import { UploadCSVType, InventoryUploadCSVType, ItemType } from "../../../../../../util/enums";
import { isShipmentTracking } from "../../../../../../util/validation";
import { Button } from "../../../../../Form";
import GET_INPUT_COLUMNS from "./getInputColumns";
import useStyles from "./styles";

type ShipmentOnBoarding = {
  container_numbers: string;
  projectId: number | undefined;
  eventType: string;
  companyId: number;
  branchId: number | undefined;
};

type InputUploadProps = {
  onChangeFile?: (file: CSVValueProps[]) => void;
  onOpenErrorModal?: () => void;
  onChangeUploadedError?: (errors: string[]) => void;
  onChangeCsvName?: (csvName: string) => void;
  isUploaded?: boolean;
  uploadFile?: CSVValueProps[];
  columnType: UploadCSVType;
  inputRef: React.RefObject<HTMLInputElement>;
};

const regexToMatchWithDateFormatDayMonthYear =
  /^(?:(?:31(\/)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(\/)(?:0?[13-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:29(\/)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\/)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)?\d{2})$/gm;

const regexToMatchWithDateFormatYearMonthDay =
  /^\d{4}-(0?[1-9]|1[012])-(0?[1-9]|[12][0-9]|3[01])$/gm;

export const InputUpload = ({
  onChangeFile,
  onOpenErrorModal,
  uploadFile,
  inputRef,
  columnType,
  isUploaded,
  onChangeUploadedError,
  onChangeCsvName,
}: InputUploadProps) => {
  const { t: translate } = useTranslation();

  const { currentUser } = useAppState();

  const classes = useStyles({ columnType });

  const COLUMNS = GET_INPUT_COLUMNS(translate);

  const { setUploadedDataCSV, setIsOpenUploadModal, setCurrentColumnType } = useOnBoardingContext();
  const [fileName, setFileName] = useState<string>("");

  const {
    getFleetTypeList,
    getCompanyFirstBranch,
    updateAccess,
    updateFleetList,
    updateInstructions,
    updateMaterials,
    updateService,
    updateMembers,
    updateItems,
    updateExceptions,
    updateExceptionLocations,
    updatePreparationMethods,
    updateLocations,
    updatePackageTypes,
    updateModes,
    updateShipments,
  } = useActionOnBoarding();
  const { data: firstBranch } = getCompanyFirstBranch();

  const [inputUploadedDataCSV, setInputUploadedDataCSV] = useState<CSVValueProps[]>(
    uploadFile || []
  );

  const currentBranch = useMemo(() => {
    return currentUser?.branches?.at(0) ?? firstBranch;
  }, [currentUser, firstBranch]);

  const onRequestSuccess = () => {
    setIsOpenUploadModal(false);
    setUploadedDataCSV([]);
    setCurrentColumnType(undefined);
  };

  const convertStringToNumber = (value: string): number | string => {
    const number = Number(value);

    if (!Number.isNaN(number)) {
      return number;
    }

    return value;
  };

  const valueIsMatchWithDateFormat = (value: string): boolean => {
    const dateIsValid = value.match(regexToMatchWithDateFormatYearMonthDay);

    return !!dateIsValid;
  };

  const convertValueToDateFormat = (value: string): string => {
    const dateIsValid = value.match(regexToMatchWithDateFormatDayMonthYear);

    if (!dateIsValid) return value;

    const [date] = dateIsValid;

    const [dayString, monthString, yearString] = date.split("/");

    const day = Number(dayString);
    const month = Number(monthString);
    const year = Number(yearString);

    const newDate = format(new Date(year, month, day), "yyyy-MM-dd");

    return newDate;
  };

  const convertValueToBoolean = (value: string): boolean | string => {
    if (!value) return false;

    if (value.toLocaleLowerCase() === "x") return true;

    return value;
  };

  const checkIfTheValueIsValid = async (
    columnName: UploadCSVType | InventoryUploadCSVType,
    data: CSVValueProps[]
  ): Promise<boolean> => {
    if (!data || data?.length === 0) {
      const textTemplateIsInvalid = translate("csvTemplateIsInvalid");
      const templateIsInvalid = [textTemplateIsInvalid];

      if (onChangeUploadedError) onChangeUploadedError(templateIsInvalid);

      return false;
    }

    const error: string[] = [];
    let hasError: boolean = false;

    for (const [index, row] of Array.from(data.entries())) {
      for (const key in row) {
        if (row.hasOwnProperty.call(row, key)) {
          const column = COLUMNS[columnName].find((column) => column.key === key);

          if (!column) {
            const textTemplateIsInvalid = translate("csvTemplateIsInvalid");

            const templateIsInvalid = [textTemplateIsInvalid];

            if (onChangeUploadedError) onChangeUploadedError(templateIsInvalid);

            return false;
          }

          if (!row[key] && row[key] !== 0 && column.isRequired) {
            const stringError = `Line ${index + 1} - ${
              column?.label ? `${column.label} - ` : ""
            } ${translate("isRequired")}`;

            error.push(stringError);
            hasError = true;
          }

          if (column.type === "fleetId" && !isNumber(row[key])) {
            const fleetIdError = `${translate("line")} ${index + 1} - ${
              column?.label ? `${column.label} - ` : ""
            } ${translate("mustBeOneOfTheValidOptions")}`;

            error.push(fleetIdError);
            hasError = true;
          }

          if (column.type === "boolean" && !isBoolean(row[key])) {
            const booleanError = `${translate("line")} ${index + 1} - ${
              column?.label ? `${column.label.replace(/[^a-zA-Z?{1}() ]/g, "")} - ` : ""
            } ${translate("mustBeFilledInWithXOrLeftEmpty")}`;

            error.push(booleanError);
            hasError = true;
          }

          if (column.type === "number" && row[key] && !isNumber(row[key])) {
            const numberError = `${translate("line")} ${index + 1} - ${
              column?.label ? `${column.label} - ` : ""
            } ${translate("mustBeANumber")}`;

            error.push(numberError);
            hasError = true;
          }

          if (
            column.type === "date" &&
            row[key] &&
            !valueIsMatchWithDateFormat(row[key] as string)
          ) {
            const numberError = `${translate("line")} ${index + 1} - ${
              column?.label ? `${column.label} - ` : ""
            } ${translate("mustBeADateWithTheFormatDdMmYyyy")}`;

            error.push(numberError);

            hasError = true;
          }
        }
      }
    }

    if (onChangeUploadedError) onChangeUploadedError(error);

    return !hasError;
  };

  const [inventoryUploads, setInventoryUploads] = useState<boolean[]>([
    false,
    false,
    false,
    false,
    false,
    false,
    false,
  ]);

  // Helper to update inventory upload status by index
  const updateInventoryUploadStatus = (index: number) => {
    setInventoryUploads((prev) => {
      const updated = [...prev];
      updated[index] = true;
      return updated;
    });
  };

  useEffect(() => {
    if (!inventoryUploads.includes(false)) {
      setInputUploadedDataCSV([{ type: UploadCSVType.INVENTORY_LIST }] as CSVValueProps[]);
      onRequestSuccess();
    }
  }, [inventoryUploads]);

  const mountCSVToArray = async (columnName: UploadCSVType | InventoryUploadCSVType, data?: string) => {
    if (!data) return;

    setIsOpenUploadModal(true);

    const rows = data.split("\n").map((row) => row.replace("\r", ""));

    if (rows && rows.length > 1) {
      const csvArray: CSVValueProps[] = [];
      let fleetTypeList: FleetType[] | undefined;

      if (columnType === UploadCSVType.FLEET) {
        fleetTypeList = await getFleetTypeList();
      }

      for (let index = 1, { length } = rows; index < length; index += 1) {
        const row = rows[index]?.trim();

        if (!row || row.length === 0) {
          continue;
        }

        const content = row.split(",");
        const requiredContentLength = COLUMNS[columnName]?.filter((column) => column.isRequired).length;

        if (content && content.length >= requiredContentLength) {
          const csvObject: CSVValueProps = {};

          for (const [index, column] of Array.from(COLUMNS[columnName].entries())) {
            const value = content[index]?.trim();

            if (column.type === "fleetId") {
              const fleetId = fleetTypeList?.find(
                (fleetType) => fleetType.value.toLocaleLowerCase() === value?.toLocaleLowerCase()
              )?.id;

              csvObject[column.key] = fleetId;
            } else if (column.type === "boolean") {
              csvObject[column.key] = convertValueToBoolean(value);
            } else if (column.type === "number") {
              csvObject[column.key] = value ? convertStringToNumber(value) : value;
            } else if (column.type === "date") {
              csvObject[column.key] = value ? convertValueToDateFormat(value) : value;
            } else {
              csvObject[column.key] = value ? value.replace(/"/g, "") : "";
            }
          }

          csvArray.push(csvObject);
        }
      }

      const isValueValid = await checkIfTheValueIsValid(columnName, csvArray);

      setCurrentColumnType(columnType);

      if (isValueValid) {
        setUploadedDataCSV(csvArray);
        if (columnType && currentUser?.companyId) {
          const newValue = csvArray.map((csvValue) => {
            if (columnType === UploadCSVType.FLEET && currentUser?.branches?.length) {
              return {
                ...csvValue,
                companyId: currentUser?.companyId,
                branchId: currentUser?.branches[0].id,
              };
            }

            if (columnType === UploadCSVType.STAFF && currentBranch) {
              return {
                ...csvValue,
                companyId: currentUser.companyId,
                branchId: currentBranch.id,
                branches: currentBranch ? [currentBranch] : [],
              };
            }

            if (columnName === InventoryUploadCSVType.EXCEPTION) {
              return {
                ...csvValue,
                type: "Exception",
                companyId: currentUser?.companyId,
              };
            }

            if (columnName === InventoryUploadCSVType.EXCEPTION_LOCATION) {
              return {
                ...csvValue,
                type: "Location",
                companyId: currentUser?.companyId,
              };
            }

            if (columnName === InventoryUploadCSVType.PREPARATION_METHOD) {
              return {
                ...csvValue,
                type: "Descriptive",
                companyId: currentUser?.companyId,
              };
            }

            if (columnName === InventoryUploadCSVType.MODE) {
              return {
                ...csvValue,
                value: csvValue?.name,
                companyId: currentUser?.companyId,
              };
            }

            if (columnName === InventoryUploadCSVType.ITEMS) {
              return {
                ...csvValue,
                type: csvValue?.type || ItemType.REMOVALS,
                companyId: currentUser?.companyId,
              };
            }

            // Individual Inventory upload types
            if (columnType === UploadCSVType.INVENTORY_EXCEPTION) {
              return {
                ...csvValue,
                type: "Exception",
                companyId: currentUser?.companyId,
              };
            }

            if (columnType === UploadCSVType.INVENTORY_EXCEPTION_LOCATION) {
              return {
                ...csvValue,
                type: "Location",
                companyId: currentUser?.companyId,
              };
            }

            if (columnType === UploadCSVType.INVENTORY_PREPARATION_METHOD) {
              return {
                ...csvValue,
                type: "Descriptive",
                companyId: currentUser?.companyId,
              };
            }

            if (columnType === UploadCSVType.INVENTORY_MODE) {
              return {
                ...csvValue,
                value: csvValue?.name,
                companyId: currentUser?.companyId,
              };
            }

            if (columnType === UploadCSVType.ACCESS) {
              return {
                ...csvValue,
                value: csvValue?.name,
                companyId: currentUser?.companyId,
              };
            }

            return { ...csvValue, companyId: currentUser?.companyId };
          });

          if (columnType === UploadCSVType.ACCESS) {
            updateAccess(newValue as AccessSettings[], currentUser.companyId, (accesses) => {
              if (accesses && accesses?.length > 0) {
                setInputUploadedDataCSV(accesses as CSVValueProps[]);
                onRequestSuccess();

                return;
              }

              setIsOpenUploadModal(false);
            });
          } else if (columnType === UploadCSVType.FLEET) {
            updateFleetList(newValue as Fleet[], currentUser.companyId, (fleet) => {
              if (fleet && fleet?.length > 0) {
                setInputUploadedDataCSV(fleet as unknown as CSVValueProps[]);
                onRequestSuccess();

                return;
              }

              setIsOpenUploadModal(false);
            });
          } else if (columnType === UploadCSVType.MATERIALS) {
            updateMaterials(newValue as Material[], currentUser.companyId, (materials) => {
              if (materials && materials?.length > 0) {
                setInputUploadedDataCSV(materials as unknown as CSVValueProps[]);
                onRequestSuccess();

                return;
              }

              setIsOpenUploadModal(false);
            });
          } else if (columnType === UploadCSVType.INSTRUCTIONS) {
            updateInstructions(newValue as Instruction[], currentUser.companyId, (instructions) => {
              if (instructions && instructions?.length > 0) {
                setInputUploadedDataCSV(instructions as unknown as CSVValueProps[]);
                onRequestSuccess();

                return;
              }

              setIsOpenUploadModal(false);
            });
          } else if (columnType === UploadCSVType.SERVICES) {
            updateService(newValue as Service[], currentUser.companyId, (service) => {
              if (service && service?.length > 0) {
                setInputUploadedDataCSV(service as unknown as CSVValueProps[]);
                onRequestSuccess();

                return;
              }

              setIsOpenUploadModal(false);
            });
          } else if (columnType === UploadCSVType.STAFF) {
            const staffsParsed = newValue as Staff[];
            const membersParsed = staffsParsed.map(
              (staff) =>
                ({
                  ...staff,
                  residencyStatus: "Unknown",
                  status: "Active",
                } as MemberStaffWithoutTimezoneId)
            );

            updateMembers(membersParsed, currentUser.companyId, (members) => {
              if (members?.length > 0) {
                setInputUploadedDataCSV(members as unknown as CSVValueProps[]);
                onRequestSuccess();

                return;
              }

              setIsOpenUploadModal(false);
            });
          } else if (columnType === UploadCSVType.SHIPMENT) {
            const containerNumber: string[] = [];

            const data = newValue.map((value) => ({
              ...value,
              projectId: currentUser?.projects[0].id,
              eventType: currentUser?.projects[0].name,
            }));

            for (const value of data) {
              const shipment: ShipmentOnBoarding = value as ShipmentOnBoarding;

              if (!isContainer(shipment.container_numbers)) {
                containerNumber.push(
                  `${translate("containerNumberIsInvalid")} - ${shipment.container_numbers}`
                );
              }
            }

            if (containerNumber.length > 0) {
              setIsOpenUploadModal(false);
              if (onChangeUploadedError) onChangeUploadedError(containerNumber);
              if (onOpenErrorModal) onOpenErrorModal();

              return;
            }

            updateShipments(data as Shipment[], currentUser.companyId, (shipment) => {
              if (shipment && shipment?.length > 0) {
                setInputUploadedDataCSV(shipment as unknown as CSVValueProps[]);
                onRequestSuccess();
              }
            });
          } else if (
            columnType === UploadCSVType.INVENTORY_LIST &&
            columnName === InventoryUploadCSVType.ITEMS
          ) {
            updateItems(newValue as Item[], currentUser.companyId, (items) => {
              if (items && items.length > 0) {
                updateInventoryUploadStatus(0);
                return;
              }

              setIsOpenUploadModal(false);
            });
          } else if (
            columnType === UploadCSVType.INVENTORY_LIST &&
            columnName === InventoryUploadCSVType.EXCEPTION
          ) {
            updateExceptions(
              newValue as IExceptionSymbol[],
              currentUser.companyId,
              (exceptions) => {
                if (exceptions && exceptions.length > 0) {
                  updateInventoryUploadStatus(1);
                  return;
                }

                setIsOpenUploadModal(false);
              }
            );
          } else if (
            columnType === UploadCSVType.INVENTORY_LIST &&
            columnName === InventoryUploadCSVType.EXCEPTION_LOCATION
          ) {
            updateExceptionLocations(
              newValue as ILocationSymbol[],
              currentUser.companyId,
              (exceptionLocations) => {
                if (exceptionLocations && exceptionLocations.length > 0) {
                  updateInventoryUploadStatus(2);
                  return;
                }

                setIsOpenUploadModal(false);
              }
            );
          } else if (
            columnType === UploadCSVType.INVENTORY_LIST &&
            columnName === InventoryUploadCSVType.PREPARATION_METHOD
          ) {
            updatePreparationMethods(
              newValue as IDescriptiveSymbol[],
              currentUser.companyId,
              (preparationMethods) => {
                if (preparationMethods && preparationMethods.length > 0) {
                  updateInventoryUploadStatus(3);
                  return;
                }

                setIsOpenUploadModal(false);
              }
            );
          } else if (
            columnType === UploadCSVType.INVENTORY_LIST &&
            columnName === InventoryUploadCSVType.LOCATION
          ) {
            updateLocations(newValue as Room[], currentUser.companyId, (locations) => {
              if (locations && locations.length > 0) {
                updateInventoryUploadStatus(4);
                return;
              }

              setIsOpenUploadModal(false);
            });
          } else if (
            columnType === UploadCSVType.INVENTORY_LIST &&
            columnName === InventoryUploadCSVType.PACKAGE_TYPE
          ) {
            updatePackageTypes(newValue as ItemTypes[], currentUser.companyId, (itemTypes) => {
              if (itemTypes && itemTypes.length > 0) {
                updateInventoryUploadStatus(5);
                return;
              }

              setIsOpenUploadModal(false);
            });
          } else if (
            columnType === UploadCSVType.INVENTORY_LIST &&
            columnName === InventoryUploadCSVType.MODE
          ) {
            updateModes(newValue as JobMode[], currentUser.companyId, (modes) => {
              if (modes && modes.length > 0) {
                updateInventoryUploadStatus(6);
                return;
              }

              setIsOpenUploadModal(false);
            });
          } else if (columnType === UploadCSVType.INVENTORY_LOCATION) {
            updateLocations(newValue as Room[], currentUser.companyId, (locations) => {
              if (locations && locations.length > 0) {
                setInputUploadedDataCSV(locations as unknown as CSVValueProps[]);
                onRequestSuccess();
                return;
              }
              setIsOpenUploadModal(false);
            });
          } else if (columnType === UploadCSVType.INVENTORY_PACKAGE_TYPE) {
            updatePackageTypes(newValue as ItemTypes[], currentUser.companyId, (itemTypes) => {
              if (itemTypes && itemTypes.length > 0) {
                setInputUploadedDataCSV(itemTypes as unknown as CSVValueProps[]);
                onRequestSuccess();
                return;
              }
              setIsOpenUploadModal(false);
            });
          } else if (columnType === UploadCSVType.INVENTORY_PREPARATION_METHOD) {
            updatePreparationMethods(
              newValue as IDescriptiveSymbol[],
              currentUser.companyId,
              (preparationMethods) => {
                if (preparationMethods && preparationMethods.length > 0) {
                  setInputUploadedDataCSV(preparationMethods as unknown as CSVValueProps[]);
                  onRequestSuccess();
                  return;
                }
                setIsOpenUploadModal(false);
              }
            );
          } else if (columnType === UploadCSVType.INVENTORY_MODE) {
            updateModes(newValue as JobMode[], currentUser.companyId, (modes) => {
              if (modes && modes.length > 0) {
                setInputUploadedDataCSV(modes as unknown as CSVValueProps[]);
                onRequestSuccess();
                return;
              }
              setIsOpenUploadModal(false);
            });
          } else if (columnType === UploadCSVType.INVENTORY_EXCEPTION) {
            updateExceptions(
              newValue as IExceptionSymbol[],
              currentUser.companyId,
              (exceptions) => {
                if (exceptions && exceptions.length > 0) {
                  setInputUploadedDataCSV(exceptions as unknown as CSVValueProps[]);
                  onRequestSuccess();
                  return;
                }
                setIsOpenUploadModal(false);
              }
            );
          } else if (columnType === UploadCSVType.INVENTORY_EXCEPTION_LOCATION) {
            updateExceptionLocations(
              newValue as ILocationSymbol[],
              currentUser.companyId,
              (exceptionLocations) => {
                if (exceptionLocations && exceptionLocations.length > 0) {
                  setInputUploadedDataCSV(exceptionLocations as unknown as CSVValueProps[]);
                  onRequestSuccess();
                  return;
                }
                setIsOpenUploadModal(false);
              }
            );
          } else setIsOpenUploadModal(false);
        }
      } else {
        setIsOpenUploadModal(false);
        if (onOpenErrorModal) onOpenErrorModal();
      }
    }
  };

  const handleInventoryValidation = (inventory: { [x: string]: string }) => {
    const inventoryKeys = Object.keys(inventory);

    inventoryKeys.forEach((key: string) => {
      mountCSVToArray(key as keyof typeof COLUMNS, inventory[key]);
    });
  };

  const handleChangeFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event?.target && event?.target?.files && event?.target?.files?.length > 0) {
      const csv = event.target.files[0];

      setFileName(csv.name);

      if (onChangeCsvName) onChangeCsvName(csv.name);

      const reader = new FileReader();

      if (columnType === UploadCSVType.INVENTORY_LIST) {
        reader.onload = (evt: any) => {
          const bstr = evt.target.result;
          const workbook = XLSX.read(bstr, { type: "binary" });
          const inventory: { [x: string]: string } = {};
          const inventorySheets = Object.values(InventoryUploadCSVType);

          inventorySheets.forEach((sheet: InventoryUploadCSVType) => {
            const worksheet = workbook.Sheets[sheet];

            const data = XLSX.utils.sheet_to_csv(worksheet, { blankrows: false, strip: true });

            inventory[sheet] = data;
          });

          handleInventoryValidation(inventory);
        };
        reader.readAsBinaryString(csv);
      } else {
        reader.onloadend = () => {
          mountCSVToArray(columnType, reader.result?.toString());
        };
        reader.readAsText(csv);
      }

      if (inputRef?.current) {
        inputRef.current.value = "";
      }
    }
  };

  const isShipment = isShipmentTracking(currentUser?.company);

  useEffect(() => {
    if (inputUploadedDataCSV && inputUploadedDataCSV.length > 0 && onChangeFile) {
      onChangeFile(inputUploadedDataCSV);
    }
  }, [inputUploadedDataCSV]);

  return (
    <>
      <div className={classes.uploadInput}>
        {/* eslint-disable-next-line no-nested-ternary */}
        {(inputUploadedDataCSV && inputUploadedDataCSV.length > 0) || isUploaded ? (
          <>
            {isShipment ? (
              <>
                {fileName.length && (
                  <Button className={classes.fileNameButton} disabled>
                    <GrDocumentText size={14} className={classes.mr12} />
                    <span>{fileName}</span>
                  </Button>
                )}
                <Button className={classes.uploadButton}>
                  <span>{translate("editFile")}</span>
                </Button>
              </>
            ) : (
              <span>{translate("editFile")}</span>
            )}
          </>
        ) : isShipment ? (
          <Button className={classes.uploadButton}>
            <FiUpload size={14} className={classes.mr12} />
            <span>{translate("upload")}</span>
          </Button>
        ) : (
          <FiUpload color="#308CDB" />
        )}
      </div>

      <input
        type="file"
        ref={inputRef}
        style={{ display: "none" }}
        value=""
        accept={
          columnType === UploadCSVType.INVENTORY_LIST
            ? ".xlsx"
            : ".csv"
        }
        onChange={handleChangeFile}
      />
    </>
  );
};
